import { supertypeClass, property } from '@haventech/amorphic';
import _ from 'lodash';
import { Party } from './Party';
import { Policy } from './Policy';
import { Address } from './Address';
import { Assumptions } from '../static/Assumptions';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import PubSub from 'pubsub-js';
import { SubjectManager } from '../subjectManager/SubjectManager';
import { Financials } from './Financials';
import { ExistingPolicies } from './ExistingPolicies';
import { PartyRelation } from './PartyRelation';
import { Person as GQLPerson } from '@hit/hit-api-client';
import { FlexibleObj } from '../interfaces/FlexibleObj';
import { ResidentialAsset } from './ResidentialAsset';
import { CommonUtils } from '../../js/static/CommonUtils';
import { ESignSessionMetadata } from './ESignSessionMetadata';
import { JuvenilePropsByAge } from '../interfaces/CommonTypes';

@supertypeClass({ toClient: true, toServer: true })
export class Person extends Party implements GQLPerson {
  @property({ isLocal: true, persist: false })
  residencyStatusTrigger() {
    if (this.isSignerType()) {
      return;
    }

    this.customer.application.primaryPolicy.faceAmountTrigger();
  }

  @property()
  bucket: string;

  @property({
    length: 40,
    rule: ['name', 'required'],
    logChanges: true,
    comment: 'First name of the individual/person',
  })
  firstName: string;

  get emailName() {
    return this.firstName;
  }

  @property({ length: 40, rule: 'name', comment: 'Middle name of the individual/person' })
  middleName: string;

  @property({ length: 40, rule: ['name', 'required'], logChanges: true, comment: 'Last name of the individual/person' })
  lastName: string;

  @property({ rule: ['email', 'required'], logChanges: true })
  email: string;

  @property({ comment: 'Residential address details of the individual', getType: () => Address })
  residentialAddress: Address;

  @property({ getType: () => Address })
  verificationAddress: Address;

  @property()
  jointOwnershipType: string;

  @property({ getType: () => Address })
  examAddress: Address;

  initExamAddress(customer: Customer) {
    this.examAddress = Address.new(customer);
  }

  @property({
    rule: 'required',
    values: ['1', '2'],
    descriptions: { '1': 'Male', '2': 'Female' },
    comment: 'Gender of the individual. Domain of Values {"1": "Male", "2": "Female"}',
  })
  gender: '1' | '2';

  @property({ isLocal: true })
  private _hasNoDriversLicense: boolean;

  @property({ isLocal: true })
  get hasNoDriversLicense(): boolean {
    return !this.hasDriversLicense;
  }

  set hasNoDriversLicense(value: boolean) {
    this.hasDriversLicense = !value;
    this.hasDriversLicenseTrigger();
  }

  @property({ rule: ['SSN'], sensitiveData: true, comment: 'Social Security Number of the Party' })
  SSN: string;

  @property({ rule: ['EIN'], sensitiveData: true })
  EIN: string;

  @property({
    rule: ['required', 'numericInt'],
    min: 18,
    max: 70,
    validate: 'isWithin(18, 70)',
    comment: 'Age of the individual when the policy is applied.',
  })
  age: number;

  @property({
    rule: ['DOB', 'required'],
    sensitiveData: true,
    compositeValidators: ['policyHasValidDOBForProduct', 'policyHasValidTerm', 'policyHasValidFaceAmount'],
    comment: 'Date of Birth of the Individual/Person',
  })
  dob: Date;

  @property({ rule: ['required', 'text'], comment: 'City of Birth' })
  birthCity: string;

  @property({ rule: ['required', 'text'] })
  currentInsurer: string;

  @property({ rule: ['required', 'text'] })
  currentInsurerGroup: string;

  @property({ rule: ['required', 'text'], sensitiveData: true })
  currentInsuranceID: string;

  @property({
    rule: ['required', 'text'],
    values: Assumptions.countryValues,
    comment: 'Country of Birth',
  })
  birthCountry: string = 'US';

  @property({
    rule: ['required', 'text'],
    values: Assumptions.stateValues,
    comment: 'State where the Person was Born',
  })
  birthState: string;

  @property({ getType: () => PartyRelation })
  familyMembers: Array<PartyRelation> = [];

  @property({
    rule: 'required',
    values: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    descriptions: {
      '1': 'Spouse or partner',
      '2': 'Parent',
      '3': 'Legal Guardian',
      '4': 'Grandparent',
      '5': 'Sibling',
      '6': 'Child',
      '7': 'Grandchild',
      '8': 'Niece or nephew',
      '9': 'Aunt or uncle',
      '10': 'Other individual',
      '11': 'Mother', // Used in Policy inside ownerTypeSelectionTrigger
      '12': 'Father', // Used in Policy inside ownerTypeSelectionTrigger
      '13': 'Female Legal Guardian', // Used in Policy inside ownerTypeSelectionTrigger
      '14': 'Male Legal Guardian', // Used in Policy inside ownerTypeSelectionTrigger
    },
    comment: 'Relationship to insured',
  })
  relationshipToInsured: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'True indicates that the individual has a drivers license',
  })
  hasDriversLicense: boolean = true;

  @property({
    values: ['drivers', 'passport', 'stateID', 'none', 'other'],
    descriptions: function () {
      const policy = this.customer.application.primaryPolicy;
      const isNYPolicy = policy.getApplicationState() === 'NY';

      const options = {
        passport: 'Passport',
        stateID: 'State-Issued ID',
        none: 'No form of ID',
      };

      if (isNYPolicy) {
        if (this.hasDriversLicense) {
          options['drivers'] = 'US Drivers License';
        }
        return options;
      } else {
        options.stateID = 'State ID';
        options['drivers'] = 'US Drivers License';
        options['other'] = 'Other';
        return options;
      }
    },
    rule: ['required'],
  })
  idType: string = 'drivers';

  @property({ rule: ['isValidId'], sensitiveData: true, comment: 'ID number associated with the idType' })
  idNumber: string;

  @property({ validate: 'notEmpty()' })
  idOtherType: string;

  @property({ rule: 'required', values: Assumptions.countryValues, comment: 'Country that issued the ID' })
  idCountry: string;

  @property({ values: Assumptions.stateValues, comment: 'State that issued the ID' })
  idState: string;

  @property({ rule: ['futuredate'], comment: 'Expiry date of the ID' })
  idExpires: Date;

  @property({ rule: ['required', 'date'], comment: 'Issue Date of the ID' })
  idIssuedDate: Date;

  @property({
    rule: 'required',
    values: ['citizen', 'none'],
    descriptions: function () {
      if (this.customer && this.customer.application && this.customer.application.primaryPolicy) {
        return Person.getResidencyStatusDescriptions();
      }
    },
    comment: 'Indicates if the individual/person is a US citizen or not',
  })
  residencyStatus: string;

  @property({
    values: function (): string[] {
      const state =
        this.customer &&
        this.customer.application &&
        this.customer.application.primaryPolicy &&
        this.customer.application.primaryPolicy.getApplicationState();

      if (state === 'NY') {
        return ['US_citizen_abroad', 'foreign_citizen_US'];
      }

      return ['US_citizen_abroad', 'foreign_citizen_US', 'Other_citizen'];
    },
    descriptions: function (): FlexibleObj<string> {
      const state =
        this.customer &&
        this.customer.application &&
        this.customer.application.primaryPolicy &&
        this.customer.application.primaryPolicy.getApplicationState();

      if (state === 'NY') {
        return {
          US_citizen_abroad: 'US citizen living abroad',
          foreign_citizen_US: 'Foreign citizen living in the US',
        };
      }

      return {
        US_citizen_abroad: 'US citizen living abroad',
        foreign_citizen_US: 'Foreign citizen living in the US',
        Other_citizen: 'Other',
      };
    },
    comment: 'What is your residency and citizenship status',
  })
  resiCatgdStatus: string;

  @property({ rule: 'required' })
  residencyDetail: string;

  @property({ rule: ['required', 'text'] })
  visaType: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Has your stay in the US been continuous',
  })
  residencyContinuous: string;

  @property()
  residencyContinuousDetail: string;

  @property({
    rule: ['numericPositive'],
    validate: 'isWithin(1,100)',
  })
  residencyYears: number;

  @property({
    rule: ['required', 'text'],
    values: Assumptions.countryValues,
    comment: 'Two digit citizenship country code of the individual. ',
  })
  citizenshipCountry: string;

  @property()
  childrenResident: boolean;

  @property()
  parentResident: boolean;

  @property()
  partnerResident: boolean;

  @property()
  siblingResident: boolean;

  @property()
  noResident: boolean;

  @property()
  otherResident: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you own personal assets in the US',
  })
  residencyAsset: string;

  @property({ getType: () => ResidentialAsset })
  residencyAssetList: Array<ResidentialAsset> = [];

  @property()
  residentialProperty: boolean;

  @property()
  commercialProperty: boolean;

  @property()
  investmentAccount: boolean;

  @property()
  bankAccount: boolean;

  @property()
  otherProperty: boolean;

  @property()
  otherAssetProperty: boolean;

  @property({
    comment: 'Details for other personal assets in the US',
  })
  otherAssetPropertyDetails: string;

  @property({ rule: ['currencyZero', 'required'] })
  assetValue: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Indicates whether resident plans to live outside the US within the next two years or not',
  })
  residencyPlans: string;

  @property({
    rule: ['required'],
    comment: 'Details about resident plans to live outside the US within the next two years or not',
  })
  residencyPlansDetails: string;

  @property({ rule: ['text'], values: Assumptions.countryValues })
  residencyCountry: string;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  famAbsenceUSRes: boolean;

  @property({ validate: 'notEmpty()', values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  absenceUsRes: boolean;

  @property({
    rule: ['numericPositive'],
    validate: 'isWithin(1,100)',
  })
  outsideUSResidentialYears: number;

  @property({
    values: ['Personal', 'Family', 'Business', 'Education', 'Missionary'],
    descriptions: {
      Personal: 'Personal',
      Family: 'Family',
      Business: 'Business',
      Education: 'Education',
      Missionary: 'Missionary Work',
    },
  })
  resAbsencePurpose: string;

  @property()
  resAbsenceMission: string;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hasVisa: string;

  @property()
  visaNumber: string;

  @property({ rule: ['date', 'futuredate'], comment: 'Date visa expires' })
  visaExpireDate: Date;

  @property({ rule: ['futureshortdate'], comment: 'Date resident expects to leave' })
  residentLeaveDate: Date;

  @property()
  documentationDetail: string;

  @property({ rule: ['TIN'], sensitiveData: true, comment: 'Tax Identification Number' })
  TIN: string;

  @property()
  noSSN: boolean;

  @property()
  noTIN: boolean;

  @property({
    isLocal: true,
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
  })
  hasDifferentLegalName: string;

  @property({
    isLocal: true,
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
  })
  hasPreviousAddress: string;

  @property({ isLocal: true })
  legalFirstName: string;

  @property({ isLocal: true })
  legalMiddleName: string;

  @property({ isLocal: true })
  legalLastName: string;

  @property({ isLocal: true })
  idCheckPage1: boolean = true;

  /**
   * START PROPERTIES TO BE USED FOR COVERPATH 2020 FILING
   */
  @property({ rule: ['date', 'futuredate'], comment: 'Date documentation expires for non U.S. citizens' })
  documentationExpireDate: Date;

  @property({ comment: 'Type of documemntation for Non U.S. citizens' })
  documentationType: string;

  @property({ comment: 'Non U.S. citizen permanent resident card number' })
  permanentResidentCardNumber: string;

  @property({ comment: 'Non U.S. citizen employment authorization document number' })
  employmentAuthDocNumber: string;

  @property({ comment: 'Type of documemntation for Non U.S. citizens with other documentation type' })
  otherDocumentationType: string;

  @property({ comment: 'Documentation number for non U.S. citizen with other documentation type' })
  otherDocNumber: string;
  /**
   * END PROPERTIES TO BE USED FOR COVERPATH 2020 FILING
   */

  noTINTrigger() {
    if (this.noTIN) {
      this.TIN = null;
    }
  }

  TINTrigger() {
    if (
      this.TIN &&
      this.customer &&
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      Utils.isCoverpathPlatform(this.customer.application.primaryPolicy.getChannelCode())
    ) {
      this.noTIN = false;
    }
  }

  @property({
    getType: () => Customer,
    comment:
      'Links this party to the main customer record indicating this party is ' +
      'either an owner, insured, payer, beneficiary, etc',
  })
  customer: Customer;

  @property()
  idIssuedDateDate: Date;

  @property({ getType: () => ESignSessionMetadata })
  esignMetadata: ESignSessionMetadata;

  getIsUSCitizenOrEntity() {
    return this.isCitizen();
  }

  getIsEntityOrTrust(): boolean {
    return false;
  }

  noSSNTrigger() {
    if (this.noSSN) {
      this.SSN = null;
    }
  }

  familyMembersTrigger() {
    // These number values should be updated to minAge and maxAge constants in Product.ts
    // TODO HL-15837
    const age = this.age || this.getAge();
    if (age >= 0 && age <= 17) {
      if (this.familyMembers.length === 0) {
        this.familyMembers = [PartyRelation.new(this.customer)];
      }
    } else {
      this.familyMembers = [];
    }
  }

  SSNTrigger() {
    if (
      this.SSN &&
      this.customer &&
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      Utils.isCoverpathPlatform(this.customer.application.primaryPolicy.getChannelCode())
    ) {
      this.noSSN = false;
    }
  }

  residencyAssetTrigger() {
    if (this.residencyAsset === 'true') {
      this.residencyAssetList.push(ResidentialAsset.new(this.customer));
    } else {
      this.residencyAssetList = [];
    }
  }

  constructor() {
    super();
  }

  static new(customer?, copyFinancials?: boolean) {
    return new Person().init(customer, copyFinancials);
  }

  static getResidencyStatusDescriptions() {
    return {
      citizen: 'US Citizen & Resident',
      none: 'Other',
    };
  }

  init(customer?, copyFinancials?: boolean) {
    super.init();

    if (customer) {
      this.customer = customer;
      this.financials = Financials.new(customer);
      this.existingPolicies = ExistingPolicies.new(customer);
      this.esignMetadata = ESignSessionMetadata.new();
      if (
        copyFinancials &&
        customer &&
        customer.application &&
        customer.application.primaryPolicy &&
        customer.application.primaryPolicy.owner &&
        customer.application.primaryPolicy.owner.financials
      ) {
        this.financials = customer.application.primaryPolicy.owner.financials.createCopy((obj, prop, template) => {
          if (template.amorphicClassName.match(customer.amorphicClass.amorphicClassName)) {
            return [this.customer];
          }
          return null;
        });
      }
      // TODO remove this pointer
      this.customer.persons.push(this);
    }

    return this;
  }

  getRelationshipToInsured(): string {
    return this.__descriptions__('relationshipToInsured')[this.relationshipToInsured];
  }

  getMiddleInitial() {
    return this.middleName.substr(0, 1);
  }

  getPerStirpesFullName() {
    if (this.customer.application.primaryPolicy.beneficiaryPerStirpes) {
      const name = this.firstName + (this.middleName ? ' ' + this.middleName : '') + ' ' + this.lastName;

      return name.length > 3 ? name + ', per stirpes' : '';
    } else {
      return this.firstName + (this.middleName ? ' ' + this.middleName : '') + ' ' + this.lastName;
    }
  }

  getPerStirpesContingentFullName() {
    if (this.customer.application.primaryPolicy.contingentBeneficiaryPerStirpes) {
      const name = this.firstName + (this.middleName ? ' ' + this.middleName : '') + ' ' + this.lastName;

      return name.length > 3 ? name + ', per stirpes' : '';
    } else {
      return this.firstName + (this.middleName ? ' ' + this.middleName : '') + ' ' + this.lastName;
    }
  }

  get fullName() {
    if (!this.firstName && !this.middleName && !this.lastName) {
      return '';
    }
    return (
      (this.firstName ? this.firstName : '') +
      (this.middleName ? ' ' + this.middleName : '') +
      ' ' +
      (this.lastName ? this.lastName : '')
    );
  }

  getFullName() {
    return this.fullName;
  }

  getFullNameOfOwner() {
    return this.fullName;
  }

  static getFullNameFromRaw(row) {
    return (
      (row.firstName ? row.firstName : '') +
      (row.middleName ? ' ' + row.middleName : '') +
      ' ' +
      (row.lastName ? row.lastName : '')
    );
  }

  get firstNameLastName(): string {
    const firstNameLastName: string = `${this.firstName ? this.firstName : ''}${
      this.lastName ? ' ' + this.lastName : ''
    }`;
    return firstNameLastName.trim();
  }

  getInitials() {
    return this.firstName && this.lastName
      ? this.firstName.charAt(0).toUpperCase() + this.lastName.charAt(0).toUpperCase()
      : '';
  }

  getIdExpires() {
    return Utils.dateToMdy(this.idExpires);
  }

  toString() {
    if (!(this.firstName && this.lastName)) {
      return '';
    }

    return this.getFullName();
  }

  getChooserDisplay() {
    return this.toString();
  }

  getGenderDescription(): string {
    return this.__descriptions__('gender')[this.gender];
  }

  /**
   * Returns a single character - F or M based on the person's gender.
   */
  getGenderDescriptionShort(): string {
    return this.getGenderDescription() ? this.getGenderDescription().substr(0, 1) : '';
  }

  genderTrigger() {
    if (this.isPrimaryApplicant() && this.customer.application && this.customer.application.primaryPolicy) {
      this.customer.application.primaryPolicy.waiverPremium = null;
    }
    if (this.isInsured()) {
      if (this.isFemale()) {
        this.customer.application.primaryPolicy.insured.personalHistory.hasProstatectomy = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hasProstatectomyTrigger();
      } else if (this.isMale()) {
        this.customer.application.primaryPolicy.insured.personalHistory.hadMastectomy = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadMastectomyTrigger();

        this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDCervicitis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDFibrocystic = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDOvarianCyst = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDOBenign = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDOBenignDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDPolycystic = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDPolycysticDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDCongenitalMalform = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDNotSure = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadIDNotSureTrigger();
        this.customer.application.primaryPolicy.insured.personalHistory.hadAbnormalUterineBleeding = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadAbnormalUterineBleedingDiagnosis = null;

        this.customer.application.primaryPolicy.insured.personalHistory.hadComplicatedPregnancy = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPGestationalDiabities = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPGestationalDiabitiesDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPPreclampsia = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPPreclampsiaDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPBenign = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPHighBloodPressure = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPHeartLiverKidneyProblems = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPHeartLiverKidneyProblemsDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPSeizures = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPSeizuresDiagnosis = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPNotSure = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPNotSureTrigger();
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPHELLP = null;
        this.customer.application.primaryPolicy.insured.personalHistory.hadCPHELLPDiagnosis = null;

        this.customer.application.primaryPolicy.insured.riskFactors.hasSpeechDelayOrDevelopmentalIssue = null;
        this.customer.application.primaryPolicy.insured.riskFactors.hasSpeechDelayOrDevelopmentalIssueTrigger();
        this.customer.application.primaryPolicy.insured.riskFactors.isPregnant = null;
      }
    }
  }

  isMale() {
    return this.gender === '1';
  }

  isFemale() {
    return this.gender === '2';
  }

  isInsured() {
    return (
      this.customer && this.customer.application && this.customer.application.primaryPolicy.insured.person === this
    );
  }

  isOwner() {
    return this.customer && this.customer.application && this.customer.application.primaryPolicy.owner === this;
  }

  isSignerType() {
    return this.amorphicGetClassName() === 'Signer';
  }

  getSsn(includeHyphens) {
    if (!this.SSN) {
      return '';
    }

    const storedWithHyphens = _.includes(this.SSN, '-');
    const alreadyFormatted = (includeHyphens && storedWithHyphens) || (!includeHyphens && !storedWithHyphens);

    if (alreadyFormatted) {
      return this.SSN;
    } else if (includeHyphens) {
      return addHyphens(this.SSN);
    } else {
      return this.SSN.replace(/-/g, '');
    }

    function addHyphens(ssn) {
      return [ssn.substr(0, 3), ssn.substr(3, 2), ssn.substr(5, 4)].join('-');
    }
  }

  getSsnLast4() {
    if (!this.SSN) {
      return '';
    }
    return this.SSN.substr(this.SSN.length - 4, 4);
  }

  getSsnCensored(includeHyphens) {
    if (!this.SSN) {
      return '';
    }
    return (includeHyphens ? '***-**-' : '*****') + this.getSsnLast4();
  }

  getTaxIdType() {
    // Only Entity templates have tax ids
    // Person templates have SSNs
    if (this.SSN) {
      return 'SSN';
    } else if (this.EIN) {
      return 'EIN';
    } else {
      return 'Unknown';
    }
  }

  ageTrigger() {
    this.dob = new Date();
    this.dob.setFullYear(new Date().getFullYear() - this.age);
    this.customer.capitalNeeds.ageTrigger(this);
  }

  isPrimaryApplicant() {
    return this.customer && this.customer.primaryCustomer && this.customer.primaryCustomer.person === this;
  }

  dobGetYear() {
    return this.dob ? this.dob.getFullYear() : '';
  }

  dobGetMonth() {
    return this.dob ? this.dob.getMonth() + 1 : '';
  }

  dobGetMonthPlusSlash() {
    return this.dob ? this.dob.getMonth() + 1 + '/' : '';
  }

  dobGetDay() {
    return this.dob ? this.dob.getDate() : '';
  }

  dobGetDayPlusSlash() {
    return this.dob ? this.dob.getDate() + '/' : '';
  }

  dobTrigger() {
    this.age = this.getAge();

    const checkAndRemoveWaiver = (coverpathPolicy: Policy) => {
      // we would like to set the waiver (and the selectedQuote.waiver) to null
      // in case it's already selected and the policy is either (1)Term or (2)WholeLife or (3)part of a joint app
      // At this point these are the only options, so someone could omit this check completely.
      // I put it there to be super explicit in case we have another situation in the future.
      const notWopEligible =
        !CommonUtils.isWopEligible(coverpathPolicy) &&
        (coverpathPolicy.isTermLifePolicy() ||
          coverpathPolicy.isWholeLifePolicy() ||
          coverpathPolicy.isPartOfJointApp());
      if (coverpathPolicy.waiver && notWopEligible) {
        coverpathPolicy.waiver = null;
        coverpathPolicy.premiumBreakdown.waiver = 0;
        coverpathPolicy.waiverTrigger();
      }
    };

    const removeInvalidJuvenileProps = (coverpathPolicy: Policy) => {
      const invalidPropsByAge: JuvenilePropsByAge = CommonUtils.invalidJuvenilePropsByAge();

      for (const age in invalidPropsByAge) {
        if (coverpathPolicy.isInsuredYoungerThanOrEqualToAge(parseFloat(age))) {
          invalidPropsByAge[age].personalHistory.forEach((prop: string) => {
            coverpathPolicy.insured.personalHistory[prop] = null;
          });
          invalidPropsByAge[age].riskFactors.forEach((prop: string) => {
            coverpathPolicy.insured.riskFactors[prop] = null;
          });
        }
      }
    };

    const checkAndRemoveGir = (coverPathPolicy: Policy) => {
      const notGirEligible = !coverPathPolicy.isValidGIRPolicy();
      if (coverPathPolicy.hasGirRider && notGirEligible) {
        coverPathPolicy.hasGirRider = null;
      }
    };

    const checkAndRemoveRtr = (coverPathPolicy: Policy) => {
      const notRtrEligible = !coverPathPolicy.isValidRTRPolicy();
      if (coverPathPolicy.hasRtrRider && notRtrEligible) {
        coverPathPolicy.hasRtrRider = null;
      }
    };

    const removeEffectiveDate = (coverPathPolicy: Policy) => {
      coverPathPolicy.effectiveDate = null;
    };

    if (this.isPrimaryApplicant()) {
      const primaryPolicy = this.customer.application && this.customer.application.primaryPolicy;
      if (primaryPolicy) {
        const insuredDob =
          primaryPolicy && primaryPolicy.insured && primaryPolicy.insured.person && primaryPolicy.insured.person.dob;

        primaryPolicy.waiverPremium = null;

        if (insuredDob) {
          if (primaryPolicy.isCoverpathPolicy() || primaryPolicy.isMMDirectPolicy()) {
            if (
              primaryPolicy.insured.person.hasDriversLicense === null &&
              primaryPolicy.isInsuredOlderThanOrEqualToAge(16.5)
            ) {
              /**
               * it is possible the insured was not old enough for us to ask the Driver's License question
               * so 'hasDriversLicense' = null, but now DOB was changed so insured is >= 16.5 years old and we will ask the Driver's License question
               *
               * need to set 'hasDriversLicense' = true
               * so the 'No Driver's License' checkbox does not default as checked
               * see ngOnInit() in custom-drivers-licnese.component.ts
               */
              primaryPolicy.insured.person.hasDriversLicense = true;
            }

            if (primaryPolicy.insuredIsJuvenile()) {
              removeInvalidJuvenileProps(primaryPolicy);
            }

            removeEffectiveDate(primaryPolicy);
            checkAndRemoveWaiver(primaryPolicy);
            checkAndRemoveGir(primaryPolicy);
            checkAndRemoveRtr(primaryPolicy);
          }

          const secondaryPolicy = this.customer.application.secondaryPolicy;
          if (secondaryPolicy && secondaryPolicy.isCoverpathPolicy()) {
            removeEffectiveDate(secondaryPolicy);
            checkAndRemoveWaiver(secondaryPolicy);
            checkAndRemoveGir(primaryPolicy);
            checkAndRemoveRtr(primaryPolicy);
          }

          this.familyMembersTrigger();
        }
      }
    }
  }

  getAge(asOfDate?) {
    return this.dob ? Utils.yearsBetween(this.dob, asOfDate || Utils.now()) : null;
  }

  getInsuranceAge(asOfDate?) {
    asOfDate = asOfDate || Utils.now();

    const insDate = this.getInsuranceAgeDate(asOfDate);

    return Utils.yearsBetween(this.dob, asOfDate) + (insDate <= asOfDate ? 1 : 0);
  }

  getInsuranceAgeDate(asOfDate) {
    asOfDate = asOfDate || Utils.now();

    return Utils.getCurrentMidYearDate(this.dob, asOfDate);
  }

  getDOB(): string {
    return Utils.dateToMdy(this.dob);
  }

  getPlaceOfBirth(): string {
    return this.birthCountry === 'US'
      ? Assumptions.stateValues[this.birthState] + ', United States'
      : this.getCountryValues()[this.birthCountry];
  }

  bornInTheUSA(): boolean {
    return this.birthCountry === 'US';
  }

  // This is relevant to underwriting constraints on the face amount.
  incomeMultiple(): number {
    const roundedAge = this.getInsuranceAge();

    if (roundedAge <= 40) {
      return 35;
    } else if (roundedAge <= 45) {
      return 30;
    } else if (roundedAge <= 50) {
      return 25;
    } else if (roundedAge <= 55) {
      return 20;
    } else if (roundedAge <= 60) {
      return 15;
    } else if (roundedAge <= 69) {
      return 10;
    } else {
      return 5;
    }
  }

  hasDriversLicenseTrigger() {
    if (this.hasDriversLicense === false) {
      this.idNumber = this.idState = this.idExpires = null;
      this.idType = null;
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_person_idNumber_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_person_idNumber_changed', '');
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_person_idState_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_person_idState_changed', '');
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_person_idExpires_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_person_idExpires_changed', '');
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_person_idType_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_person_idType_changed', '');
    } else {
      this.idType = 'drivers';
    }
  }

  emailTrigger() {
    const application = this.customer ? this.customer.application : null;
    const policy = application ? application.primaryPolicy : null;
    if (policy) {
      const insuredIsJuvenile = policy.insuredIsJuvenile();
      const hasSigner = policy.insured.hasSigner;
      if (insuredIsJuvenile && !hasSigner && policy.owner.email) {
        policy.insured.person.email = policy.owner.email;
      }
    }
  }

  idTypeTrigger() {
    if (this.idType === null) {
      this.idType = 'drivers';
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_person_idType_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_person_idType_changed', '');
    }
  }

  idNumberTrigger() {
    if (this.idNumber && this.idType === 'drivers') {
      this.hasDriversLicense = true;
    }
  }

  idOtherTypeDependsOn() {
    return this.idType === 'other';
  }

  idCountryDependsOn() {
    return this.idType !== 'drivers';
  }

  idStateDependsOn() {
    return this.idType === 'drivers';
  }

  idExpiresGetYear() {
    return this.idExpires ? this.idExpires.getFullYear() : '';
  }

  idExpiresGetMonth() {
    return this.idExpires ? this.idExpires.getMonth() + 1 : '';
  }

  idExpiresGetDay() {
    return this.idExpires ? this.idExpires.getDate() : '';
  }

  idIssuedDateGetYear() {
    return this.idIssuedDateDate ? this.idIssuedDate.getFullYear() : '';
  }

  idIssuedDateGetMonth() {
    return this.idIssuedDateDate ? this.idIssuedDate.getMonth() + 1 : '';
  }

  idIssuedDateGetDay() {
    return this.idIssuedDateDate ? this.idIssuedDate.getDate() : '';
  }

  getDriversLicenseNumber() {
    return this.idType === 'drivers' ? this.idNumber : '';
  }

  getDriversLicenseState() {
    return this.idType === 'drivers' ? this.idState : '';
  }

  getDriversLicenseExpMonth() {
    return this.idType === 'drivers' ? this.idExpiresGetMonth() : '';
  }

  getDriversLicenseExpDay() {
    return this.idType === 'drivers' ? this.idExpiresGetDay() : '';
  }

  getDriversLicenseExpYear() {
    return this.idType === 'drivers' ? this.idExpiresGetYear() : '';
  }

  getAlternateIDNumber() {
    return this.idType !== 'drivers' ? this.idNumber : '';
  }

  getAlternateIDState() {
    return this.idType !== 'drivers' ? this.idState : '';
  }

  getAlternateIDCountry() {
    return this.idType !== 'drivers' ? this.idState : '';
  }

  getAlternateIDExpMonth() {
    return this.idType !== 'drivers' ? this.idExpiresGetMonth() : '';
  }

  getAlternateIDExpDay() {
    return this.idType !== 'drivers' ? this.idExpiresGetDay() : '';
  }

  getAlternateIDExpYear() {
    return this.idType !== 'drivers' ? this.idExpiresGetYear() : '';
  }

  isResidentAlien() {
    return this.residencyStatus === 'residentAlien';
  }

  getResidencyOtherForDS() {
    return this.residencyStatus !== 'citizen' ? 'Other' : '';
  }

  isCitizen(): boolean {
    return this.residencyStatus === 'citizen';
  }

  visaTypeDependsOn() {
    return this.residencyStatus && this.residencyStatus !== 'citizen';
  }

  getResidencyYears() {
    if (this.residencyYears) {
      return this.residencyYears + ' years';
    } else {
      return '';
    }
  }

  getResidencyStatus() {
    return this.residencyStatus === 'none' || this.residencyStatus === 'residentAlien' ? 'No' : 'Yes';
  }

  getCitizenshipCountry() {
    const country = _.find(
      _.keys(this.__values__('citizenshipCountry')),
      function (key) {
        return key === this.citizenshipCountry;
      }.bind(this)
    );

    return this.__values__('citizenshipCountry')[country];
  }

  getRelativeResidents() {
    let residents = '';

    residents += this.noResident ? 'None' : '';
    residents += this.childrenResident ? 'Children  ' : '';
    residents += this.parentResident ? 'Parent  ' : '';
    residents += this.partnerResident ? 'Partner   ' : '';
    residents += this.siblingResident ? 'Sibling ' : '';

    return residents;
  }

  getResidencyAsset() {
    let assets = 'I own ';

    assets += this.residentialProperty ? 'Residential Property ' : '';
    assets += this.commercialProperty ? 'Commercial Property ' : '';
    assets += this.investmentAccount ? 'Investment Accounts ' : '';
    assets += this.bankAccount ? 'Bank Accounts ' : '';
    assets += this.otherProperty ? 'and Other Property ' : '';

    return assets;
  }

  getCountryValues() {
    const primaryPolicy: Policy = this.customer && this.customer.application && this.customer.application.primaryPolicy;
    const channelCode: string = primaryPolicy && primaryPolicy.getChannelCode();
    const isCoverpathPlatform: boolean = Utils.isCoverpathPlatform(channelCode);
    const isNewYorkApplication: boolean = primaryPolicy && primaryPolicy.getApplicationState() === 'NY';
    return isCoverpathPlatform && isNewYorkApplication ? Assumptions.countryValuesNY : Assumptions.countryValues;
  }

  documentationTypeExists(): boolean {
    return Utils.exists(this.documentationType);
  }
}
